import { AUDIENCE_CARD_TYPE, PasscodeList, SelectedMergeAudienceLists } from '@/types/registration.types';
import Container from '@ui/container/container';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';
import Text from '@ui/text/text/text';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';
import Button from '@ui/buttons/button/button';
import Link from '@ui/links/link';
import Popover from '@ui/popover/popover';
import { Activity, useRef, useState } from 'react';
import { getViewPath, downloadUsers, removePasscodeList, downloadPasscodeList } from '../../utils';
import CreateListModal from '../listsv2/create-list-modal';
import MergeListModal from './merge-lists-modal';
import AudienceConfirmRemove from '../audience-confirm-remove';
import toastTrigger from '@ui/toast/toast-trigger';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { selectProjectName, selectWorkingProjectRegistrationSettings } from '@/store/slices/project/selectors';
import { EToastTypes } from '@ui/toast/toast.types';
import { useProjectUuidParam } from '@/utils/use-project-uuid-param';
import useLanguageParams from '@/utils/use-language-params';
import globalWindowSize from '@/utils/global-window-size';

type AudienceActionsProps = {
  audienceCardType: AUDIENCE_CARD_TYPE;
  passcodeListId?: number;
  passcodeList?: PasscodeList;
  memberCount?: string | number;
  name?: string;
  isListView?: boolean;
  /** campaign-only lists aren't attached to the event, so they can't be removed from it */
  isCampaignOnly?: boolean;
};

const AudienceActions = ({
  audienceCardType,
  passcodeListId,
  passcodeList,
  memberCount,
  name,
  isListView = false,
  isCampaignOnly = false,
}: AudienceActionsProps) => {
  const popoverPortalRef = useRef<HTMLDivElement | null>(null);
  const projectUuid = useProjectUuidParam();
  const language = useLanguageParams();
  const dispatch = useAppDispatch();
  const channel = useAppSelector(getActiveChannel);
  const projectName = useAppSelector(selectProjectName);
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);

  const [isCreateListModalOpen, setIsCreateListModalOpen] = useState(false);
  const [isMergeListModalOpen, setIsMergeListModalOpen] = useState(false);
  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);

  const getInitialMergeAudienceLists = (): SelectedMergeAudienceLists => {
    if (audienceCardType === AUDIENCE_CARD_TYPE.PASSCODE_LIST && passcodeListId) {
      return { sourceAudienceLists: [passcodeListId] };
    }
    return { sourceRegistrantLists: [audienceCardType] };
  };

  const handleDownloadUsers = async () => {
    if (!channel || !projectUuid) return;
    try {
      await downloadUsers({ channel, projectUuid, audienceCardType, projectName });
    } catch {
      toastTrigger('Download failed', {
        description: 'Your audience list failed to generate',
        type: EToastTypes.error,
        autoClose: 10000,
      });
    }
  };

  const handleRemoveList = () => {
    if (!registrationSettings || !passcodeListId) return;
    removePasscodeList({ registrationSettings, passcodeListIds: [passcodeListId], dispatch });
  };

  const handleDownloadPasscodeList = async () => {
    if (!passcodeList) return;
    try {
      await downloadPasscodeList({ passcodeList, name });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Your audience list failed to generate';

      toastTrigger('Download failed', {
        description: message,
        type: EToastTypes.error,
        autoClose: 10000,
      });
    }
  };

  const hasMembers = Number(memberCount ?? passcodeList?.member_count ?? 0) > 0;

  return (
    <>
      <Popover
        closeOnClick
        width="180px"
        containerClassName="radius-12"
        cycleWithArrowKeys
        portalEl={popoverPortalRef}
        renderTriggerButton={({ open }) => (
          <Button
            className="flex fd-row jc-center ai-center radius-full padding-0"
            width="32px"
            height="32px"
            design={isListView ? undefined : TButtonAndLinkDesigns.LocalBttnSecInverted}
            fixedState={open ? 'hover' : undefined}
          >
            <Icon name={ICONS.MENU_VERTICAL2} size={12} color="var(--local-bttn-sec-default-icotext)" />
          </Button>
        )}
      >
        {({ triggerBounds }) => {
          const spaceBelow = globalWindowSize.innerHeight - (triggerBounds?.bottom ?? 0);
          const openUpward = spaceBelow < 160;
          return (
            <Container
              background={'var(--global-cont-3)'}
              className="position-fixed z-20 overflow-y-auto"
              borderRadius="12px"
              width="164px"
              style={{
                top: openUpward ? undefined : (triggerBounds?.top ?? 0),
                bottom: openUpward ? globalWindowSize.innerHeight - (triggerBounds?.top ?? 0) : undefined,
                left: (triggerBounds?.left ?? 0) + (triggerBounds?.width ?? 0),
                transform: openUpward ? 'translateX(-100%)' : 'translate(-100%, 40px)',
                minWidth: 'max-content',
              }}
            >
              <Link
                to={getViewPath({ audienceCardType, passcodeListId, projectUuid, language })}
                className="flex fd-row jc-flex-start ai-center gap-8 padding-h-16 padding-v-8"
              >
                <Icon name={ICONS.SHOW_EYE} size={12} color="var(--global-ico-text-2)" />
                <Text className="P2" design="GlobalIcoText2">
                  View
                </Text>
              </Link>

              <Activity mode={hasMembers ? 'visible' : 'hidden'}>
                <Button
                  className="flex fd-row jc-center ai-center gap-8 padding-h-16 padding-v-8"
                  onClick={() => setIsMergeListModalOpen(true)}
                >
                  <Icon name={ICONS.ADD_REGISTRATION} size={12} color="var(--global-ico-text-2)" />
                  <Text className="P2" design="GlobalIcoText2">
                    Add to a List
                  </Text>
                </Button>
              </Activity>

              <Button
                className="flex fd-row jc-center ai-center gap-8 padding-h-16 padding-v-8"
                onClick={() =>
                  audienceCardType === AUDIENCE_CARD_TYPE.PASSCODE_LIST
                    ? handleDownloadPasscodeList()
                    : handleDownloadUsers()
                }
              >
                <Icon name={ICONS.DOWNLOAD} size={12} color="var(--global-ico-text-2)" />
                <Text className="P2" design="GlobalIcoText2">
                  Download
                </Text>
              </Button>

              <Activity
                mode={audienceCardType === AUDIENCE_CARD_TYPE.PASSCODE_LIST && !isCampaignOnly ? 'visible' : 'hidden'}
              >
                <Button
                  className="flex fd-row jc-center ai-center gap-8 padding-h-16 padding-v-8"
                  onClick={() => setIsConfirmRemoveOpen(true)}
                >
                  <Icon name={ICONS.TRASH} size={12} color="var(--global-error-400)" />
                  <Text className="P2" color="var(--global-error-400)">
                    Remove
                  </Text>
                </Button>
              </Activity>
            </Container>
          );
        }}
      </Popover>

      <CreateListModal
        open={isCreateListModalOpen}
        setOpen={setIsCreateListModalOpen}
        audienceCardType={audienceCardType}
        passcodeListToClone={passcodeListId}
      />
      <MergeListModal
        open={isMergeListModalOpen}
        setOpen={setIsMergeListModalOpen}
        initialSelectedMergeAudienceLists={getInitialMergeAudienceLists()}
        onCreateNewList={() => setIsCreateListModalOpen(true)}
      />
      <AudienceConfirmRemove
        open={isConfirmRemoveOpen}
        onClose={() => setIsConfirmRemoveOpen(false)}
        onConfirm={handleRemoveList}
      />
    </>
  );
};

export default AudienceActions;
